<template>
  <div class="page-wrapper">
    <div class="content container-fluid">
      <!-- Header -->
      <div class="page-header">
        <div class="row align-items-center w-100">
          <div class="col">
            <h3 class="page-title">Quản lý Nghỉ phép</h3>
            <ul class="breadcrumb">
              <li class="breadcrumb-item"><a href="/">Dashboard</a></li>
              <li class="breadcrumb-item active">Nghỉ phép (Admin)</li>
            </ul>
          </div>
          <div class="col-auto d-flex align-items-center gap-8">
            <button class="btn btn-outline-secondary mr-2" @click="resetFilters" :disabled="loading">
              <i class="fa fa-undo mr-1"></i> Đặt lại
            </button>
            <button class="btn btn-primary" @click="onSearch" :disabled="loading">
              <i class="fa fa-search mr-1"></i> Tìm kiếm
            </button>
          </div>
        </div>
      </div>

      <!-- Filters -->
      <div class="card shadow-soft mb-3">
        <div class="card-body">
          <div class="row">
            <div class="col-lg-2 col-sm-6 mb-2">
              <label class="small">Ngày tạo từ</label>
              <input type="date" class="form-control" v-model="filters.createdFrom">
            </div>
            <div class="col-lg-2 col-sm-6 mb-2">
              <label class="small">Ngày tạo đến</label>
              <input type="date" class="form-control" v-model="filters.createdTo">
            </div>
            <div class="col-lg-2 col-sm-6 mb-2">
              <label class="small">Trạng thái</label>
              <select class="form-control" v-model="filters.status">
                <option value="ALL">Tất cả</option>
                <option value="PENDING">Chờ duyệt</option>
                <option value="APPROVED">Đã duyệt</option>
                <option value="REJECTED">Từ chối</option>
                <option value="CANCELLED">Đã hủy</option>
              </select>
            </div>
            <div class="col-lg-3 col-sm-6 mb-2">
              <label class="small">Nhân viên / Lý do</label>
              <input type="text" class="form-control" v-model.trim="filters.keyword" placeholder="Tìm theo tên, mã NV, lý do…">
            </div>
            <div class="col-lg-3 col-sm-12 mb-2 d-flex align-items-end">
              <div class="btn-group w-100">
                <button class="btn btn-outline-success" :disabled="!bulk.ids.length || loading" @click="openBulkNote(true)">
                  <i class="fa fa-check mr-1"></i> Duyệt hàng loạt
                </button>
                <button class="btn btn-outline-danger" :disabled="!bulk.ids.length || loading" @click="openBulkNote(false)">
                  <i class="fa fa-times mr-1"></i> Từ chối hàng loạt
                </button>
                <button class="btn btn-outline-dark" :disabled="loading" @click="exportList">
                  <i class="fa fa-download mr-1"></i> Xuất Excel/CSV
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Table -->
      <div class="card shadow-soft">
        <div class="card-body p-0">
          <div class="table-responsive">
            <table class="table table-striped custom-table m-0">
              <thead>
                <tr>
                  <th style="width:36px">
                    <input type="checkbox" :checked="allChecked" @change="toggleAll($event)">
                  </th>
                  <th style="min-width:90px;"># Đơn</th>
                  <th style="min-width:220px;">Nhân viên</th>
                  <th style="min-width:150px;">Khoảng ngày</th>
                  <th style="min-width:120px;">Tổng (ngày)</th>
                  <th style="min-width:320px;">Chi tiết</th>
                  <th style="min-width:220px;">Lý do</th>
                  <th style="min-width:130px;">Trạng thái</th>
                  <th style="min-width:180px;">Duyệt bởi</th>
                  <th style="min-width:170px;">Ngày tạo</th> <!-- NEW -->
                  <th class="text-right" style="min-width:220px;">Hành động</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="loading">
                  <td :colspan="11" class="text-center p-4">
                    <i class="fa fa-spinner fa-spin mr-2"></i> Đang tải dữ liệu…
                  </td>
                </tr>

                <tr v-for="r in rows" :key="r.idNghiPhep">
                  <td>
                    <input type="checkbox" :value="r.idNghiPhep" v-model="bulk.ids" :disabled="!canSelect(r)">
                  </td>
                  <td><span class="badge badge-light">#{{ r.idNghiPhep }}</span></td>
                  <td>
                    <h2 class="table-avatar mb-0">
                      <span class="avatar avatar-xs mr-2">
                        <img v-if="r.avatarUrl" :src="r.avatarUrl" alt="">
                        <span v-else class="avatar-fallback">{{ initials(r.hoTen) }}</span>
                      </span>
                      <div class="d-inline-flex flex-column">
                        <span class="font-weight-bold">{{ r.hoTen }}</span>
                        <small class="text-muted">{{ r.maNhanVien || '—' }} · {{ r.tenPhongBan || '—' }}</small>
                      </div>
                    </h2>
                  </td>
                  <td>
                    <div class="small">
                      <i class="fa fa-calendar"></i>
                      {{ fmtDate(r.tuNgay) }} → {{ fmtDate(r.denNgay) }}
                    </div>
                  </td>
                  <td>
                    <span class="badge badge-info">{{ totalDays(r).toFixed(2) }}</span>
                  </td>
                  <td class="py-2">
                    <div class="chips">
                      <span v-for="(c, i) in r.chiTiet" :key="i" class="chip" :class="chipClass(c)">
                        <i :class="chipIcon(c)"></i> {{ chipText(c) }}
                      </span>
                      <span v-if="!r.chiTiet?.length" class="text-muted">—</span>
                    </div>
                  </td>
                  <td>
                    <div class="ellipsis-2" :title="r.lyDo || ''">{{ r.lyDo || '—' }}</div>
                  </td>
                  <td>
                    <span class="badge" :class="statusClass(r.trangThai)">{{ statusText(r.trangThai) }}</span>
                  </td>
                  <td>
                    <div class="small">
                      <div><b>{{ r.nguoiDuyet || '—' }}</b></div>
                      <div class="text-muted">{{ r.ngayDuyet ? fmtDateTime(r.ngayDuyet) : '—' }}</div>
                    </div>
                  </td>
                  <td>
                    <div class="small">{{ r.createdAt ? fmtDateTime(r.createdAt) : '—' }}</div>
                  </td>
                  <td class="text-right">
                    <div class="btn-group">
                      <button class="btn btn-sm btn-outline-secondary" @click="openDetail(r)">
                        <i class="fa fa-eye"></i>
                      </button>
                      <button class="btn btn-sm btn-outline-success" :disabled="!canApprove(r)" @click="openNote(r, true)">
                        <i class="fa fa-check"></i>
                      </button>
                      <button class="btn btn-sm btn-outline-danger" :disabled="!canApprove(r)" @click="openNote(r, false)">
                        <i class="fa fa-times"></i>
                      </button>
                      <button class="btn btn-sm btn-outline-dark" v-if="canCancel(r)" @click="cancelReq(r)">
                        <i class="fa fa-ban"></i>
                      </button>
                    </div>
                  </td>
                </tr>

                <tr v-if="!loading && rows.length===0">
                  <td :colspan="11" class="text-center text-muted p-4">Không có dữ liệu</td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Pagination -->
          <div class="p-2 d-flex align-items-center justify-content-between">
            <div class="small text-muted">
              Trang <b>{{ page.number + 1 }}</b> / {{ page.totalPages }} —
              Tổng: <b>{{ page.totalElements }}</b> đơn
            </div>
            <div>
              <div class="btn-group btn-group-sm">
                <button class="btn btn-outline-secondary" :disabled="page.number===0 || loading" @click="goPage(0)">«</button>
                <button class="btn btn-outline-secondary" :disabled="page.number===0 || loading" @click="goPage(page.number-1)">‹</button>
                <button class="btn btn-outline-secondary" :disabled="page.number>=page.totalPages-1 || loading" @click="goPage(page.number+1)">›</button>
                <button class="btn btn-outline-secondary" :disabled="page.number>=page.totalPages-1 || loading" @click="goPage(page.totalPages-1)">»</button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- /Table -->
    </div>

    <!-- Modal Chi tiết + Duyệt -->
    <div v-if="detail.open">
      <!-- ... giữ nguyên phần modal như cũ ... -->
    </div>
  </div>
</template>

<script>
import api from "@/api";
import Swal from "sweetalert2";
import { useToast } from "vue-toastification";

export default {
  name: "AdminLeaveManage",
  data() {
    const today = new Date();
    const y = today.getFullYear();
    const m = String(today.getMonth()+1).padStart(2,'0');
    const d = String(today.getDate()).padStart(2,'0');
    return {
      loading: false,
      rows: [],
      page: { number: 0, size: 10, totalElements: 0, totalPages: 1 },

      filters: {
        createdFrom: `${y}-${m}-01`,
        createdTo: `${y}-${m}-${d}`,
        status: "ALL",
        keyword: ""
      },

      bulk: { ids: [], acting: false, note: "", accept: true },

      detail: { open: false, data: null, acting: false, note: "", actionYes: true },

      toast: null
    };
  },
  computed: {
    allChecked() {
      const selectable = this.rows.filter(this.canSelect);
      return selectable.length > 0 && selectable.every(r => this.bulk.ids.includes(r.idNghiPhep));
    }
  },
  mounted() {
    this.toast = useToast ? useToast() : null;
    this.fetchList();
  },
  methods: {
    async fetchList() {
      this.loading = true;
      try {
        const params = {
          page: this.page.number,
          size: this.page.size,
          status: this.filters.status !== 'ALL' ? this.filters.status : undefined,
          createdFrom: this.filters.createdFrom || undefined,
          createdTo: this.filters.createdTo || undefined,
          keyword: this.filters.keyword || undefined
        };
        const { data } = await api.get("/api/nghi-phep/admin", { params });
        const pg = data?.data || {};
        this.rows = Array.isArray(pg.content) ? pg.content : (data?.data || []);
        if (Array.isArray(pg.content)) {
          this.page.totalElements = pg.totalElements ?? this.rows.length;
          this.page.totalPages = pg.totalPages ?? 1;
          this.page.number = pg.number ?? 0;
          this.page.size = pg.size ?? this.page.size;
        } else {
          this.page.totalElements = this.rows.length;
          this.page.totalPages = 1;
          this.page.number = 0;
        }
        this.bulk.ids = [];
      } catch (e) {
        this.rows = [];
        this.page.totalElements = 0;
        this.page.totalPages = 1;
        this.page.number = 0;
        this.toastError(e, "Không tải được danh sách nghỉ phép");
      } finally {
        this.loading = false;
      }
    },

    async loadDetail(id) {
      try {
        const { data } = await api.get(`/api/nghi-phep/${id}`);
        this.detail.data = data?.data || null;
      } catch (e) {
        this.detail.data = null;
        this.toastError(e, "Không tải được chi tiết đơn");
      }
    },

    async approve(row, accept) {
      try {
        this.detail.acting = true;
        this.detail.actionYes = !!accept;
        const id = row.idNghiPhep;
        await api.post(`/api/nghi-phep/${id}/duyet`, {
          dongY: !!accept,
          ghiChu: this.detail.note || undefined
        });
        this.toastSuccess(accept ? "Đã duyệt đơn." : "Đã từ chối đơn.");
        await this.fetchList();
        await this.loadDetail(id);
      } catch (e) {
        this.toastError(e, "Không thể cập nhật phê duyệt");
      } finally {
        this.detail.acting = false;
      }
    },

    async cancelReq(row) {
      const ok = await this.confirm("Bạn có chắc muốn hủy đơn này?");
      if (!ok) return;
      try {
        const id = row.idNghiPhep;
        await api.post(`/api/nghi-phep/${id}/huy`);
        this.toastSuccess("Đã hủy đơn.");
        if (this.detail.open) await this.loadDetail(id);
        await this.fetchList();
      } catch (e) {
        this.toastError(e, "Không thể hủy đơn");
      }
    },

    async bulkApprove(accept) {
      try {
        this.bulk.acting = true;
        const ids = this.bulk.ids.slice();
        await api.post("/api/nghi-phep/bulk-duyet", {
          ids, dongY: !!accept, ghiChu: this.bulk.note || undefined
        });
        this.toastSuccess(accept ? "Đã duyệt các đơn đã chọn." : "Đã từ chối các đơn đã chọn.");
        this.bulk.ids = [];
        this.bulk.note = "";
        await this.fetchList();
      } catch (e) {
        this.toastError(e, "Không thể cập nhật hàng loạt");
      } finally {
        this.bulk.acting = false;
      }
    },

    async exportList() {
      try {
        const params = {
          status: this.filters.status !== 'ALL' ? this.filters.status : undefined,
          createdFrom: this.filters.createdFrom || undefined,
          createdTo: this.filters.createdTo || undefined,
          keyword: this.filters.keyword || undefined
        };
        const res = await api.get("/api/nghi-phep/export", { params, responseType: "blob" }).catch(() => null);

        if (res?.data) {
          const blob = new Blob([res.data], { type: res.headers['content-type'] || "application/octet-stream" });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `nghi-phep_created_${this.filters.createdFrom || 'all'}_${this.filters.createdTo || 'all'}.xlsx`;
          document.body.appendChild(a);
          a.click();
          a.remove();
          URL.revokeObjectURL(url);
          return;
        }

        const csv = this.toCSV(this.rows);
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = "nghi-phep.csv"; a.click();
        URL.revokeObjectURL(url);
      } catch (e) {
        this.toastError(e, "Không thể xuất danh sách");
      }
    },

    // ===== UI / Helpers =====
    onSearch() {
      if (this.filters.createdFrom && this.filters.createdTo &&
          new Date(this.filters.createdTo) < new Date(this.filters.createdFrom)) {
        return this.alert("Khoảng ngày không hợp lệ", "Ngày tạo đến phải ≥ Ngày tạo từ.");
      }
      this.page.number = 0;
      this.fetchList();
    },
    resetFilters() {
      this.filters.status = "ALL";
      this.filters.keyword = "";
      this.onSearch();
    },
    goPage(n) {
      if (n < 0 || n >= this.page.totalPages) return;
      this.page.number = n;
      this.fetchList();
    },

    openDetail(r) { this.detail.open = true; this.detail.data = null; this.detail.note = ""; this.loadDetail(r.idNghiPhep); },
    closeDetail() { this.detail.open = false; this.detail.data = null; this.detail.note = ""; },

    openNote(row, accept) {
      Swal.fire({
        title: accept ? "Duyệt đơn này?" : "Từ chối đơn này?",
        input: "textarea",
        inputPlaceholder: "Ghi chú (không bắt buộc)…",
        inputAttributes: { 'aria-label': 'Ghi chú' },
        showCancelButton: true,
        confirmButtonText: accept ? "Duyệt" : "Từ chối",
        cancelButtonText: "Hủy",
        reverseButtons: true,
        customClass: { confirmButton: accept ? 'btn btn-success' : 'btn btn-danger', cancelButton: 'btn btn-light' },
        buttonsStyling: false,
        preConfirm: (note) => note
      }).then(async (res) => {
        if (res.isConfirmed) {
          this.detail.note = res.value || "";
          this.detail.data = row;
          await this.approve(row, accept);
        }
      });
    },

    openBulkNote(accept) {
      if (!this.bulk.ids.length) return;
      Swal.fire({
        title: accept ? "Duyệt các đơn đã chọn?" : "Từ chối các đơn đã chọn?",
        input: "textarea",
        inputPlaceholder: "Ghi chú chung (không bắt buộc)…",
        inputAttributes: { 'aria-label': 'Ghi chú' },
        showCancelButton: true,
        confirmButtonText: accept ? "Duyệt tất cả" : "Từ chối tất cả",
        cancelButtonText: "Hủy",
        reverseButtons: true,
        customClass: { confirmButton: accept ? 'btn btn-success' : 'btn btn-danger', cancelButton: 'btn btn-light' },
        buttonsStyling: false,
        preConfirm: (note) => note
      }).then(async (res) => {
        if (res.isConfirmed) {
          this.bulk.note = res.value || "";
          this.bulk.accept = !!accept;
          await this.bulkApprove(accept);
        }
      });
    },

    toggleAll(ev) {
      const checked = ev.target.checked;
      const selectable = this.rows.filter(this.canSelect);
      this.bulk.ids = checked ? selectable.map(r => r.idNghiPhep) : [];
    },

    canSelect(r) { return r.trangThai === 'PENDING'; },
    canApprove(r) { return r && r.trangThai === 'PENDING'; },
    canCancel(r) { return r && r.trangThai === 'PENDING'; },

    totalDays(r) {
      const list = r?.chiTiet || [];
      let sum = 0;
      for (const it of list) {
        if (!it?.buoi) continue;
        if (it.buoi === 'FULL') sum += 1;
        else if (it.buoi === 'SANG' || it.buoi === 'CHIEU') sum += 0.5;
      }
      return sum;
    },

    chipText(c) {
      const date = this.fmtDate(c.ngay);
      if (c.buoi === 'FULL') return `${date} · Cả ngày`;
      if (c.buoi === 'SANG') return `${date} · Sáng`;
      if (c.buoi === 'CHIEU') return `${date} · Chiều`;
      return `${date}`;
    },
    chipClass(c) { return { 'chip-full': c.buoi === 'FULL', 'chip-am': c.buoi === 'SANG', 'chip-pm': c.buoi === 'CHIEU' }; },
    chipIcon(c) {
      if (c.buoi === 'FULL') return 'fa fa-calendar-check-o';
      if (c.buoi === 'SANG') return 'fa fa-sun-o';
      if (c.buoi === 'CHIEU') return 'fa fa-moon-o';
      return 'fa fa-circle-o';
    },

    statusText(s) {
      return { PENDING:'Chờ duyệt', APPROVED:'Đã duyệt', REJECTED:'Từ chối', CANCELLED:'Đã hủy' }[s] || s || '—';
    },
    statusClass(s) {
      return { PENDING:'badge-warning', APPROVED:'badge-success', REJECTED:'badge-danger', CANCELLED:'badge-secondary' }[s] || 'badge-light';
    },

    fmtDate(d) {
      if (!d) return '—';
      const dt = new Date(d);
      return dt.toLocaleDateString('vi-VN', { day:'2-digit', month:'2-digit', year:'numeric' });
    },
    fmtDateTime(d) {
      if (!d) return '—';
      const dt = new Date(d);
      return dt.toLocaleString('vi-VN', { hour12: false });
    },
    initials(name) {
      const n = (name || "").trim().split(/\s+/);
      return (n[0]?.[0] || "") + (n[n.length-1]?.[0] || "");
    },

    toCSV(list) {
      const header = [
        "ID","NhanVien","MaNV","Phòng Ban","Nghỉ từ ngày","Đến ngày","Trạng thái","Người duyệt","Ngày duyệt","Lý do","Tổng ngày","Chi tiết","Ngày tạo" // NEW
      ];
      const rows = [header.join(",")];
      for (const r of list) {
        const line = [
          r.idNghiPhep,
          this.csvSafe(r.hoTen),
          this.csvSafe(r.maNhanVien),
          this.csvSafe(r.tenPhongBan),
          this.csvSafe(this.fmtDate(r.tuNgay)),
          this.csvSafe(this.fmtDate(r.denNgay)),
          this.csvSafe(this.statusText(r.trangThai)),
          this.csvSafe(r.nguoiDuyet),
          this.csvSafe(r.ngayDuyet ? this.fmtDateTime(r.ngayDuyet) : ""),
          this.csvSafe(r.lyDo),
          this.totalDays(r).toFixed(2),
          this.csvSafe((r.chiTiet||[]).map(c => this.chipText(c)).join(" | ")),
          this.csvSafe(r.createdAt ? this.fmtDateTime(r.createdAt) : "") // NEW
        ];
        rows.push(line.join(","));
      }
      return rows.join("\n");
    },
    csvSafe(v) {
      if (v == null) return "";
      const s = String(v).replace(/"/g,'""');
      if (/[",\n]/.test(s)) return `"${s}"`;
      return s;
    },

    alert(title, text) { return Swal.fire({ icon:'warning', title, text }); },
    async confirm(title) {
      const r = await Swal.fire({ icon:'question', title, showCancelButton:true, confirmButtonText:'Đồng ý', cancelButtonText:'Hủy', reverseButtons:true });
      return r.isConfirmed;
    },
    toastSuccess(msg) { this.toast ? this.toast.success(msg) : console.log(msg); },
    toastError(err, fallback) {
      const m = err?.response?.data?.message || err?.message || fallback || 'Lỗi không xác định';
      this.toast ? this.toast.error(m) : console.error(m);
    }
  }
};
</script>

<style scoped>
.shadow-soft { box-shadow: 0 8px 24px rgba(0,0,0,.06); }
.avatar { width: 32px; height: 32px; border-radius: 50%; overflow: hidden; display: inline-block; }
.avatar img { width: 100%; height: 100%; object-fit: cover; display: block; }
.avatar-fallback { width: 32px; height: 32px; display: inline-flex; align-items: center; justify-content: center; background: #eee; color: #666; font-weight: 700; border-radius: 50%; }
.ellipsis-2 { display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
.chips { display: flex; flex-wrap: wrap; gap: 6px; max-height: 78px; overflow: auto; }
.chip { display: inline-flex; align-items: center; gap: 6px; border-radius: 999px; padding: 4px 10px; font-size: 12px; border: 1px solid #e5e7eb; background: #fff; }
.chip i { font-size: 12px; }
.chip-full { background: #f0f9ff; border-color: #dbeafe; }
.chip-am   { background: #f9fafb; border-color: #e5e7eb; }
.chip-pm   { background: #fff7ed; border-color: #ffedd5; }
.gap-8 > * + * { margin-left: 8px; }
</style>
